import { ReactElement, useEffect, useMemo, useState } from 'react'

import { Button } from 'antd'
import { Trans, useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { Drawer, Flex, Text } from 'components/core'
import { ExternalLinkIcon } from 'components/Icons'
import { date } from 'configuration/dayjs'

import * as styles from './styles.scss'
import {
    ResolvedWhatsNewEntry,
    WhatsNewCategory,
    WhatsNewContentBlock,
    WhatsNewLink,
} from './whatsNewTypes'

const WHATS_NEW_PAGE_SIZE = 10

const WHATS_NEW_CATEGORY_CLASS_NAMES: Record<WhatsNewCategory, string> = {
    newFeature: styles['whats-new-entry-category-new-feature'],
    improvement: styles['whats-new-entry-category-improvement'],
    dataUpdate: styles['whats-new-entry-category-data-update'],
}

interface WhatsNewDrawerProps {
    entries: ResolvedWhatsNewEntry[]
    visible: boolean
    onClose: () => void
}

const WHATS_NEW_TEXT_COMPONENTS = {
    strong: <strong />,
}

const renderLinkComponent = (link: WhatsNewLink): ReactElement => {
    const isExternal = /^https?:\/\//.test(link.href)
    if (isExternal) {
        return <a href={link.href} target="_blank" rel="noopener noreferrer" />
    }
    return <RouterLink to={link.href} />
}

const renderContentBlock = (
    block: WhatsNewContentBlock,
    index: number
): ReactElement => {
    if (block.type === 'list') {
        const linkComponents = block.links
            ? Object.fromEntries(
                  Object.entries(block.links).map(([tag, link]) => [
                      tag,
                      renderLinkComponent(link),
                  ])
              )
            : {}
        const components = {
            ...WHATS_NEW_TEXT_COMPONENTS,
            ...linkComponents,
        }
        return (
            <ul key={index}>
                {block.body.map((item) => (
                    <li key={item}>
                        <Trans defaults={item} components={components} />
                    </li>
                ))}
            </ul>
        )
    }

    if (block.type === 'richText') {
        const components = {
            ...WHATS_NEW_TEXT_COMPONENTS,
            ...Object.fromEntries(
                Object.entries(block.links).map(([tag, link]) => [
                    tag,
                    renderLinkComponent(link),
                ])
            ),
        }
        return (
            <p key={index}>
                <Trans defaults={block.body} components={components} />
            </p>
        )
    }

    return <p key={index}>{block.body}</p>
}

export const WhatsNewDrawer = ({
    entries,
    visible,
    onClose,
}: WhatsNewDrawerProps): ReactElement => {
    const { t } = useTranslation(['common', 'navigation'])
    const [visibleCount, setVisibleCount] = useState(WHATS_NEW_PAGE_SIZE)

    useEffect(() => {
        if (visible) {
            setVisibleCount(WHATS_NEW_PAGE_SIZE)
        }
    }, [visible])

    const visibleEntries = useMemo(
        () => entries.slice(0, visibleCount),
        [entries, visibleCount]
    )
    const categoryLabels: Record<WhatsNewCategory, string> = {
        newFeature: t(
            'navigation:TopNav.WhatsNew.categories.newFeature',
            'New Feature'
        ),
        improvement: t(
            'navigation:TopNav.WhatsNew.categories.improvement',
            'Improvement'
        ),
        dataUpdate: t(
            'navigation:TopNav.WhatsNew.categories.dataUpdate',
            'Data Update'
        ),
    }
    const hasMoreEntries = visibleCount < entries.length

    const handleLoadMore = (): void => {
        setVisibleCount((count) => count + WHATS_NEW_PAGE_SIZE)
    }

    return (
        <Drawer
            title={
                <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Text variant="headingSm">
                        {t('navigation:TopNav.WhatsNew.title', "What's New")}
                    </Text>
                    <a
                        href="https://cobaltjs.zendesk.com/hc/en-us/articles/26903871457943-Release-Notes"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles['whats-new-release-notes-link']}
                    >
                        {t(
                            'navigation:TopNav.WhatsNew.releaseNotesLink',
                            'Full release notes'
                        )}
                        <ExternalLinkIcon />
                    </a>
                </Flex>
            }
            onClose={onClose}
            open={visible}
            width={460}
            rootClassName={styles.drawer}
        >
            <div className={styles['whats-new-drawer-content']}>
                {entries.length > 0 ? (
                    visibleEntries.map((entry) => (
                        <section
                            key={entry.id}
                            className={styles['whats-new-entry']}
                        >
                            <div className={styles['whats-new-entry-meta']}>
                                <span
                                    className={[
                                        styles['whats-new-entry-category'],
                                        WHATS_NEW_CATEGORY_CLASS_NAMES[
                                            entry.category
                                        ],
                                    ].join(' ')}
                                >
                                    {categoryLabels[entry.category]}
                                </span>
                                <time dateTime={entry.date}>
                                    {date(entry.date).format('LL')}
                                </time>
                            </div>
                            <Text
                                variant="headingSm"
                                element="h3"
                                margin="14px 0 8px"
                            >
                                {entry.title}
                            </Text>
                            {entry.content.map(renderContentBlock)}
                            {entry.accessStatus === 'missingPermission' && (
                                <p className={styles['whats-new-upsell-text']}>
                                    {entry.missingPermissionMessage ??
                                        t(
                                            'navigation:TopNav.WhatsNew.defaultUpsellMessage',
                                            'Contact your CSM to get access.'
                                        )}
                                </p>
                            )}
                        </section>
                    ))
                ) : (
                    <p className={styles['whats-new-empty-text']}>
                        {t(
                            'navigation:TopNav.WhatsNew.emptyText',
                            'No release notes yet.'
                        )}
                    </p>
                )}
                {hasMoreEntries && (
                    <div className={styles['whats-new-load-more']}>
                        <Button onClick={handleLoadMore}>
                            {t(
                                'navigation:TopNav.WhatsNew.loadMore',
                                'Load More'
                            )}
                        </Button>
                    </div>
                )}
            </div>
        </Drawer>
    )
}
